import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, FormControl, InputLabel, Select,
  MenuItem, TextField, Chip, SelectChangeEvent,
} from '@mui/material';
import api from '../../api/client';
import { formatDateShort, formatTime24 } from '../../utils/time';

interface AuditEntry {
  id: number;
  timestamp: string;
  user_id: number | null;
  username: string;
  action: string;
  target_type: string;
  target_id: number | null;
  target_name: string;
  old_value: string;
  new_value: string;
  details: string;
  ip_address: string;
}

const ACTION_COLORS: Record<string, string> = {
  user_login: '#2196f3',
  mobile_login: '#3f51b5',
  session_revoked: '#f44336',
  password_changed: '#ff9800',
  unit_created: '#4caf50',
  unit_status_changed: '#9c27b0',
  unit_logon: '#4caf50',
  unit_logoff: '#f44336',
  unit_location_set: '#00bcd4',
  call_created: '#ff5722',
  call_updated: '#ff9800',
};

export default function AuditLogPage() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [actions, setActions] = useState<string[]>([]);
  const [targetTypes, setTargetTypes] = useState<string[]>([]);
  const [filterAction, setFilterAction] = useState('');
  const [filterTarget, setFilterTarget] = useState('');
  const [filterUsername, setFilterUsername] = useState('');

  const loadEntries = () => {
    const params = new URLSearchParams();
    if (filterAction) params.set('action', filterAction);
    if (filterTarget) params.set('target_type', filterTarget);
    if (filterUsername) params.set('username', filterUsername);
    params.set('limit', '500');
    api.get(`/audit-logs?${params.toString()}`).then((r) => setEntries(r.data)).catch(() => {});
  };

  useEffect(() => {
    api.get('/audit-logs/actions').then((r) => setActions(r.data)).catch(() => {});
    api.get('/audit-logs/target-types').then((r) => setTargetTypes(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterAction, filterTarget, filterUsername]);

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>Audit Log</Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 180 }} size="small">
            <InputLabel>Action</InputLabel>
            <Select value={filterAction} label="Action" onChange={(e: SelectChangeEvent) => setFilterAction(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {actions.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel>Target Type</InputLabel>
            <Select value={filterTarget} label="Target Type" onChange={(e: SelectChangeEvent) => setFilterTarget(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {targetTypes.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            size="small" label="Username" value={filterUsername}
            onChange={(e) => setFilterUsername(e.target.value)}
            sx={{ minWidth: 150 }}
          />
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Time</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>IP Address</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Target</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Old Value</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>New Value</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map((e) => (
              <TableRow key={e.id} hover>
                <TableCell sx={{ fontSize: 12, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                  {formatDateShort(e.timestamp)}
                </TableCell>
                <TableCell sx={{ fontSize: 12, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                  {formatTime24(e.timestamp)}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{e.username}</TableCell>
                <TableCell sx={{ fontSize: 12, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                  {e.ip_address || '—'}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={e.action}
                    sx={{ bgcolor: ACTION_COLORS[e.action] || '#757575', color: '#fff', fontWeight: 600, fontSize: 11 }}
                  />
                </TableCell>
                <TableCell>
                  {e.target_type && (
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {e.target_type}: {e.target_name || e.target_id}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ fontSize: 12, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {e.old_value}
                </TableCell>
                <TableCell sx={{ fontSize: 12, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {e.new_value}
                </TableCell>
                <TableCell sx={{ fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {e.details}
                </TableCell>
              </TableRow>
            ))}
            {entries.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                  No audit log entries found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
